import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private baseUrl: string = 'https://localhost:4200'; // Update to production URL (e.g., 'https://learning-portal.com')

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private dom: Document
  ) {}

  updateCanonical(url: string) {
    // Remove existing canonical link to avoid duplicates
    const existingLink = this.dom.querySelector("link[rel='canonical']");
    if (existingLink) {
      existingLink.remove();
    }

    // Create and append new canonical link
    const link: HTMLLinkElement = this.dom.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.dom.head.appendChild(link);
  }

  updateTitle(title: string) {
    this.title.setTitle(title);
  }

  updatePageMeta(
    titleText: string = 'Learning-Portal - Your Education Hub',
    description: string = 'High-quality educational resources and tools at Learning-Portal.',
    url: string = this.baseUrl,
    image: string | string[] = 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png',
    videoUrl?: string,
    videoWidth: string = '1280',
    videoHeight: string = '720',
    extraTags: { name?: string; property?: string; content: string }[] = []
  ) {
    // Set default Open Graph and Twitter Card tags
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Learning-Portal' });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });

    // Update title
    if (titleText) {
      this.title.setTitle(titleText);
      this.meta.updateTag({ property: 'og:title', content: titleText });
      this.meta.updateTag({ name: 'twitter:title', content: titleText });
    }

    // Update description
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ name: 'twitter:description', content: description });
    }

    // Update URL
    if (url) {
      this.updateCanonical(url);
      this.meta.updateTag({ property: 'og:url', content: url });
      this.meta.updateTag({ name: 'twitter:url', content: url });
    }

    // Handle image (single string or array)
    if (Array.isArray(image)) {
      // Use the first image for primary og:image and twitter:image
      if (image.length > 0) {
        this.meta.updateTag({ property: 'og:image', content: image[0] });
        this.meta.updateTag({ name: 'twitter:image', content: image[0] });
        // Add additional images as og:image:0, og:image:1, etc.
        image.forEach((img, index) => {
          this.meta.updateTag({ property: `og:image:${index}`, content: img });
        });
      }
    } else if (image) {
      this.meta.updateTag({ property: 'og:image', content: image });
      this.meta.updateTag({ name: 'twitter:image', content: image });
    }

    // Handle video metadata
    if (videoUrl) {
      this.meta.updateTag({ property: 'og:video', content: videoUrl });
      this.meta.updateTag({ property: 'og:video:type', content: 'video/mp4' });
      this.meta.updateTag({ property: 'og:video:width', content: videoWidth });
      this.meta.updateTag({ property: 'og:video:height', content: videoHeight });
    }

    // Add extra tags
    if (extraTags.length) {
      extraTags.forEach(tag => this.meta.updateTag(tag));
    }
  }
}